import { lazy, Suspense, useEffect, type ReactNode } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router";
import { useAuth } from "./lib/auth";
import { FullScreenSpinner } from "./components/ui";
import AppLayout from "./components/AppLayout";
import { track, trackPageView, trackPurchase } from "./lib/analytics";

function AnalyticsTracker() {
  const loc = useLocation();
  useEffect(() => {
    trackPageView(loc.pathname + loc.search);

    const params = new URLSearchParams(loc.search);
    let dirty = false;
    if (params.get("new_user") === "1") {
      track("sign_up", { method: "google" });
      params.delete("new_user");
      dirty = true;
    }
    if (params.get("checkout") === "success") {
      void trackPurchase(params.get("session_id"));
      params.delete("checkout");
      params.delete("session_id");
      dirty = true;
    }
    if (dirty) {
      const qs = params.toString();
      const cleanUrl = loc.pathname + (qs ? `?${qs}` : "") + window.location.hash;
      window.history.replaceState(null, "", cleanUrl);
    }
  }, [loc.pathname, loc.search]);
  return null;
}

const Login = lazy(() => import("./routes/Login"));
const Signup = lazy(() => import("./routes/Signup"));
const VerifyEmail = lazy(() => import("./routes/VerifyEmail"));
const ForgotPassword = lazy(() => import("./routes/ForgotPassword"));
const ResetPassword = lazy(() => import("./routes/ResetPassword"));
const Audits = lazy(() => import("./routes/Audits"));
const NewAudit = lazy(() => import("./routes/NewAudit"));
const PublicAudit = lazy(() => import("./routes/PublicAudit"));
const AuditDetail = lazy(() => import("./routes/AuditDetail"));
const Comparisons = lazy(() => import("./routes/Comparisons"));
const ComparisonDetail = lazy(() => import("./routes/ComparisonDetail"));
const AuditHistory = lazy(() => import("./routes/AuditHistory"));
const BrandKit = lazy(() => import("./routes/BrandKit"));
const Integrations = lazy(() => import("./routes/Integrations"));
const Support = lazy(() => import("./routes/Support"));
const SettingsLayout = lazy(() => import("./components/SettingsLayout"));
const SettingsGeneral = lazy(() => import("./routes/settings/General"));
const SettingsPlans = lazy(() => import("./routes/settings/Plans"));
const SettingsBilling = lazy(() => import("./routes/settings/Billing"));
const SettingsSecurity = lazy(() => import("./routes/settings/Security"));
const SettingsApiKeys = lazy(() => import("./routes/settings/ApiKeys"));
const SettingsDanger = lazy(() => import("./routes/settings/DangerZone"));
const NotFound = lazy(() => import("./routes/NotFound"));
const AdminDashboard = lazy(() => import("./routes/admin/Dashboard"));
const AdminUsers = lazy(() => import("./routes/admin/Users"));
const AdminUserDetail = lazy(() => import("./routes/admin/UserDetail"));
const AdminMarketing = lazy(() => import("./routes/admin/Marketing"));
const AdminEmailActivity = lazy(() => import("./routes/admin/EmailActivity"));
const AdminErrors = lazy(() => import("./routes/admin/Errors"));
const AdminTests = lazy(() => import("./routes/admin/Tests"));

function ProtectedRoute({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <FullScreenSpinner />;
  if (!user) {
    return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  }
  if (!user.email_verified) {
    return <Navigate to={`/verify-email?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  }
  return <>{children}</>;
}

function AdminRoute({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <FullScreenSpinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;
  return <>{children}</>;
}

function GuestRoute({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <FullScreenSpinner />;
  if (user) return <Navigate to="/" replace />;
  return <>{children}</>;
}

export default function App() {
  return (
    <Suspense fallback={<FullScreenSpinner />}>
      <AnalyticsTracker />
      <Routes>
        <Route path="/audit" element={<PublicAudit />} />
        <Route path="/login" element={<GuestRoute><Login /></GuestRoute>} />
        <Route path="/signup" element={<GuestRoute><Signup /></GuestRoute>} />
        <Route path="/forgot-password" element={<GuestRoute><ForgotPassword /></GuestRoute>} />
        <Route path="/reset-password" element={<GuestRoute><ResetPassword /></GuestRoute>} />
        <Route path="/verify-email" element={<VerifyEmail />} />

        <Route path="/" element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
          <Route index element={<Audits />} />
          <Route path="audits" element={<Navigate to="/" replace />} />
          <Route path="audits/new" element={<NewAudit />} />
          <Route path="audits/history" element={<AuditHistory />} />
          <Route path="audits/:id" element={<AuditDetail />} />
          <Route path="compare" element={<Comparisons />} />
          <Route path="compare/:id" element={<ComparisonDetail />} />
          <Route path="brand-kit" element={<BrandKit />} />
          <Route path="brands" element={<Navigate to="/brand-kit" replace />} />
          <Route path="integrations" element={<Integrations />} />
          <Route path="settings" element={<SettingsLayout />}>
            <Route index element={<Navigate to="general" replace />} />
            <Route path="general" element={<SettingsGeneral />} />
            <Route path="plans" element={<SettingsPlans />} />
            <Route path="billing" element={<SettingsBilling />} />
            <Route path="security" element={<SettingsSecurity />} />
            <Route path="api-keys" element={<SettingsApiKeys />} />
            <Route path="danger" element={<SettingsDanger />} />
            <Route path="password" element={<Navigate to="/settings/security" replace />} />
          </Route>
          <Route path="account" element={<Navigate to="/settings/general" replace />} />
          <Route path="support" element={<Support />} />
          <Route path="contact" element={<Navigate to="/support" replace />} />

          <Route path="admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
          <Route path="admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
          <Route path="admin/users/:id" element={<AdminRoute><AdminUserDetail /></AdminRoute>} />
          <Route path="admin/marketing" element={<AdminRoute><AdminMarketing /></AdminRoute>} />
          <Route path="admin/emails" element={<AdminRoute><AdminEmailActivity /></AdminRoute>} />
          <Route path="admin/errors" element={<AdminRoute><AdminErrors /></AdminRoute>} />
          <Route path="admin/tests" element={<AdminRoute><AdminTests /></AdminRoute>} />
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}
